/**
 * Phone number validation utilities for E.164 format
 * DA-157: Mobile phone number mandatory for providers and substitutes
 * DA-163: Client-side validation must match server-side validation
 * DA-166: E.164 format validation
 */

export type PhoneValidationResult = {
  valid: boolean;
  error?: string;
};

export type OptionalPhoneValidationResult =
  | {
      valid: true;
      normalized?: string;
    }
  | {
      valid: false;
      error: string;
    };

/**
 * Validates phone number in E.164 format
 *
 * Requirements:
 * - Must start with + (country code)
 * - Must have 7-15 digits total (after removing spaces)
 * - Cannot start with +0
 * - Spaces are allowed and will be stripped
 *
 * Examples:
 * - Valid: +491701234567, +49 170 1234567, +1234567890
 * - Invalid: 01701234567 (no +), +49 (too short), +0123456 (starts with +0), +abc123 (non-numeric)
 */
export const validateE164PhoneNumber = (phone: string | undefined | null): PhoneValidationResult => {
  if (!phone || phone.trim() === '') {
    return {
      valid: false,
      error: 'Telefonnummer ist erforderlich',
    };
  }

  const trimmed = phone.trim();

  // Must start with +
  if (!trimmed.startsWith('+')) {
    return {
      valid: false,
      error: 'Telefonnummer muss mit + beginnen (internationales Format)',
    };
  }

  // Cannot start with +0
  if (trimmed.startsWith('+0')) {
    return {
      valid: false,
      error: 'Telefonnummer darf nicht mit +0 beginnen',
    };
  }

  // Remove spaces and + to check digits
  const digitsOnly = trimmed.slice(1).replace(/\s/g, '');

  // Check if all remaining characters are digits
  if (!/^\d+$/.test(digitsOnly)) {
    return {
      valid: false,
      error: 'Telefonnummer darf nur Ziffern und Leerzeichen enthalten',
    };
  }

  // Check length (7-15 digits after +)
  if (digitsOnly.length < 7) {
    return {
      valid: false,
      error: 'Telefonnummer ist zu kurz (mindestens 7 Ziffern nach +)',
    };
  }

  if (digitsOnly.length > 15) {
    return {
      valid: false,
      error: 'Telefonnummer ist zu lang (maximal 15 Ziffern nach +)',
    };
  }

  return { valid: true };
};

/**
 * Normalizes phone number by removing spaces
 * Use this before storing in database
 */
export const normalizePhoneNumber = (phone: string): string => {
  return phone.replace(/\s/g, '');
};

export const validateOptionalE164PhoneNumber = (phone: string | undefined | null, fieldLabel: string): OptionalPhoneValidationResult => {
  if (phone === undefined || phone === null || phone.trim() === '') {
    return { valid: true };
  }

  const validation = validateE164PhoneNumber(phone);
  if (!validation.valid) {
    return {
      valid: false,
      error: (validation.error || 'Telefonnummer ist ungültig').replace(/^Telefonnummer/, fieldLabel),
    };
  }

  return {
    valid: true,
    normalized: normalizePhoneNumber(phone),
  };
};

/**
 * Formats phone number for display
 * Adds spaces for readability (German format)
 * Example: +491701234567 → +49 170 1234567
 */
export const formatPhoneNumber = (phone: string): string => {
  const normalized = normalizePhoneNumber(phone);

  // German mobile numbers: +49 ### #######
  if (normalized.startsWith('+49') && normalized.length >= 12) {
    return `+49 ${normalized.slice(3, 6)} ${normalized.slice(6)}`;
  }

  // Default: Just add space after country code
  // Find where country code ends (1-3 digits after +)
  const match = normalized.match(/^(\+\d{1,3})(\d+)$/);
  if (match) {
    return `${match[1]} ${match[2]}`;
  }

  return normalized;
};
